import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Drawer, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';

export default function IKMatrixDrawer({ open, onClose, api }) {
  const [matrices, setMatrices] = useState([]);
  const [activeMatrix, setActiveMatrix] = useState(null);
  const [name, setName] = useState('default');
  const [items, setItems] = useState([{ fiscalCv: 4, coefficient: 0.57 }]);

  const load = useCallback(async () => {
    const { data } = await api.get('/api/ik/matrix');
    const list = data?.data || [];
    setMatrices(list);
    const active = list.find((m) => m.isActive) || null;
    setActiveMatrix(active);
    if (active) {
      setName(active.name || 'default');
      setItems(active.items?.length ? active.items : [{ fiscalCv: 4, coefficient: 0.57 }]);
    }
  }, [api]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const addRow = () => setItems((p) => [...p, { fiscalCv: '', coefficient: '' }]);
  const removeRow = (idx) => setItems((p) => (p.length <= 1 ? p : p.filter((_, i) => i !== idx)));
  const updateRow = (idx, key, value) => setItems((p) => p.map((r, i) => (i === idx ? { ...r, [key]: value } : r)));

  const save = async () => {
    const normalizedName = String(name || '').trim();
    if (!normalizedName) {
      window.alert('Le nom de matrice est requis.');
      return;
    }

    const seenFiscalCv = new Set();
    const normalizedItems = [];

    for (let index = 0; index < items.length; index += 1) {
      const row = items[index] || {};
      const fiscalCv = Number(row.fiscalCv);
      const coefficient = Number(row.coefficient);

      if (!Number.isInteger(fiscalCv) || fiscalCv <= 0) {
        window.alert(`Ligne ${index + 1}: CV fiscaux invalide.`);
        return;
      }

      if (!Number.isFinite(coefficient) || coefficient <= 0) {
        window.alert(`Ligne ${index + 1}: coefficient invalide.`);
        return;
      }

      if (seenFiscalCv.has(fiscalCv)) {
        window.alert(`La matrice contient plusieurs lignes pour ${fiscalCv} CV fiscaux.`);
        return;
      }

      seenFiscalCv.add(fiscalCv);
      normalizedItems.push({ fiscalCv, coefficient });
    }

    const payload = {
      name: normalizedName,
      isActive: true,
      items: normalizedItems,
    };
    if (activeMatrix?._id) await api.put(`/api/ik/matrix/${activeMatrix._id}`, payload);
    else await api.post('/api/ik/matrix', payload);
    await load();
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 460, p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Matrice IK</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        <TextField label="Nom matrice" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ mb: 2 }} />

        <Stack spacing={1.5}>
          {items.map((r, i) => (
            <Stack key={i} direction="row" spacing={1}>
              <TextField label="CV fiscaux" type="number" value={r.fiscalCv} onChange={(e) => updateRow(i, 'fiscalCv', e.target.value)} sx={{ width: 140 }} />
              <TextField label="Coefficient" type="number" value={r.coefficient} onChange={(e) => updateRow(i, 'coefficient', e.target.value)} sx={{ width: 160 }} />
              <IconButton color="error" onClick={() => removeRow(i)}><DeleteIcon /></IconButton>
            </Stack>
          ))}
        </Stack>

        <Stack direction="row" spacing={1} mt={2}>
          <Button variant="outlined" onClick={addRow}>Ajouter ligne</Button>
          <Button variant="contained" onClick={save}>Sauvegarder</Button>
        </Stack>

        <Stack spacing={1} mt={2}>
          {matrices.map((m) => (
            <Paper key={m._id} variant="outlined" sx={{ p: 1, cursor: 'pointer' }} onClick={() => { setActiveMatrix(m); setName(m.name); setItems(m.items || []); }}>
              <Typography fontWeight={600}>{m.name}{m.isActive ? ' (active)' : ''}</Typography>
            </Paper>
          ))}
        </Stack>
      </Box>
    </Drawer>
  );
}
